import { AutocompleteVirtual } from '@Wonder-Cave/ui';
import { IDropdownValue } from '@Wonder-Cave/ui/dist/Autocomplete';
import useTestNumbers, { mapTestNumberSearchParams } from '@gr/portal/hooks/useTestNumbers';
import { capByTestNumber, FilterDataTypeEnum, FilterOperatorEnum, ISearchFilter, ISearchRequest, ITestNumberDto } from '@gr/shared/models';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defaultTestNumberDropdownOptions } from '../../../HealthCheckNumbers/types';

interface ITestNumbersDropdownProps {
  value: number[] | undefined;
  onChange: (newValue?: (IDropdownValue | undefined)[]) => void;
  onBlur?: () => void;
  allClients?: boolean;
  disabled?: boolean;
  errorMessage?: string | undefined;
  showError: boolean;
  client: string;
  loading?: boolean;
}

/**
 * Dedupe key for a fetched test number. Falls back to the phone number because `id` is
 * optional on the DTO.
 */
const getTestNumberKey = (testNumber: ITestNumberDto): string =>
  testNumber.id ?? `${testNumber.number}-${testNumber.isGlobalNumber}`;

/**
 * Sorts client-specific numbers ahead of global numbers, leaving equal entries in place.
 */
const compareClientSpecificFirst = (a: IDropdownValue, b: IDropdownValue): number => {
  if (a.additionalData.isGlobalNumber === b.additionalData.isGlobalNumber) {
    return 0;
  }
  return a.additionalData.isGlobalNumber ? 1 : -1;
};

/**
 * Stand-in option for a selected number that has not been fetched, so the selection is still
 * visible and still round-trips through onChange.
 */
const buildPlaceholderOption = (number: number): IDropdownValue => ({
  label: number.toString(),
  value: number,
  additionalData: {
    isGlobalNumber: false,
    number,
  },
});

/**
 * Normalizes a label or search term for case- and whitespace-insensitive matching, matching
 * how the library filters labels when refreshOptions is not supplied.
 */
const normalizeForSearch = (text: string): string => text.toLowerCase().replace(/\s+/g, '');

/**
 * Strips everything but digits, matching how GET /v1/test-numbers normalizes the number filter
 * before its LIKE comparison.
 */
const toDigits = (text: string): string => text.replace(/\D/g, '');

/**
 * Whether an option survives the client-side filter. Labels are matched as text, and any digits
 * in the term are additionally matched against the bare phone number: the server strips
 * non-digits before searching, so a formatted term like "(555) 123-4567" — copy-pasted from the
 * test message grid — would otherwise fetch its match and then be filtered straight back out.
 * Matching on both keeps the client filter a superset of what the server returns.
 *
 * @param option - The option to test
 * @param searchTerm - The raw text currently in the search input
 */
const isSearchMatch = (option: IDropdownValue, searchTerm: string): boolean => {
  if (normalizeForSearch(option.label).includes(normalizeForSearch(searchTerm))) {
    return true;
  }

  const searchDigits = toDigits(searchTerm);
  return !!searchDigits && option.additionalData.number.toString().includes(searchDigits);
};

/**
 * TestNumbersDropdown is a reusable form component that provides a searchable dropdown
 * for selecting test message contacts/numbers. It's primarily used in messaging and
 * health check features of the Grassroots Portal.
 *
 * Key features:
 * - Fetches up to TEST_NUMBER_DROPDOWN_PAGE_SIZE (100) numbers for display, and keeps every
 *   number it has ever fetched so a narrowed search never hides previously browsed options
 * - Enforces the MAX_TEST_MESSAGE_NUMBERS (50) send cap on every selection path — clicking,
 *   typing, and Select All — because the display page size is larger than the send cap
 * - Searches partial numbers server-side and test number names client-side (the
 *   GET /v1/test-numbers endpoint only filters on number)
 * - Handles both client-specific and all-client test numbers
 * - Sorts client-specific numbers before global numbers
 *
 * @param value - Currently selected test numbers (phone numbers)
 * @param onChange - Callback function when selection changes; never receives more than
 *                   MAX_TEST_MESSAGE_NUMBERS entries
 * @param onBlur - Optional callback for blur events
 * @param disabled - Whether the dropdown is disabled
 * @param errorMessage - Error message to display if validation fails
 * @param showError - Whether to show the error message
 * @param client - Client identifier for filtering numbers
 * @param loading - Loading state indicator
 */
const TestNumbersDropdown = ({
  value,
  onChange,
  onBlur,
  disabled,
  errorMessage,
  showError,
  client,
  loading,
}: ITestNumbersDropdownProps): JSX.Element => {
  const [{
    data: testNumbersData,
    loading: testNumbersLoading,
    error: testNumbersError
  }, searchTestNumbers] =
    useTestNumbers(defaultTestNumberDropdownOptions(client));

  // Every test number fetched so far, not just the latest response. A server-side search
  // narrows the response to matches, which would otherwise drop already-browsed (and
  // already-selected) numbers out of the option list for the rest of the session.
  const [fetchedTestNumbers, setFetchedTestNumbers] = useState<ITestNumberDto[]>([]);
  // Mirrors what is actually typed in the search input so the client-side name filter can be
  // applied ourselves — supplying refreshOptions turns the library's own label filtering off.
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownContainerRef = useRef<HTMLDivElement>(null);
  // Tracks the client the component is currently showing, so a debounced search scheduled
  // before a client change can detect that it is stale — see handleSearchTestNumbers
  const latestClientRef = useRef(client);

  // A different client is a different set of authorized numbers, so nothing may carry over
  useEffect(() => {
    latestClientRef.current = client;
    setFetchedTestNumbers([]);
    setSearchTerm('');
  }, [client]);

  // AutocompleteVirtual exposes no input-change callback, and it only invokes refreshOptions once
  // its search text reaches 2 characters (@Wonder-Cave/ui/dist/index.js:6306). Deriving searchTerm
  // from refreshOptions alone therefore froze the filter whenever the input was backspaced to 0-1
  // characters: the input looked empty while the list stayed narrowed. Reading the rendered input
  // keeps the two in step at every length, and restores filtering from the first keystroke.
  // Programmatic clears (on select and on blur) fire no input event, so they are handled below.
  useEffect(() => {
    const searchInput = dropdownContainerRef.current?.querySelector('input');
    if (!searchInput) {
      return;
    }

    const handleSearchInput = (event: Event) => setSearchTerm((event.target as HTMLInputElement).value);
    searchInput.addEventListener('input', handleSearchInput);
    return () => searchInput.removeEventListener('input', handleSearchInput);
  }, []);

  useEffect(() => {
    // ExternalV1PaginatedResponse types `data` as `T[] | T`, so with T = ITestNumberDto[] the
    // element type widens to ITestNumberDto | ITestNumberDto[]. GET /v1/test-numbers always
    // returns a flat DTO array, so narrow it once here.
    const testNumbersResponse = (Array.isArray(testNumbersData?.data) ? testNumbersData.data : []) as ITestNumberDto[];
    if (testNumbersResponse.length === 0) {
      return;
    }
    setFetchedTestNumbers((previousTestNumbers) => {
      const seenKeys = new Set(previousTestNumbers.map(getTestNumberKey));
      const newTestNumbers = testNumbersResponse.filter((testNumber) => !seenKeys.has(getTestNumberKey(testNumber)));
      // Keep the same reference when nothing is new so an unchanged response can't re-render
      return newTestNumbers.length > 0 ? [...previousTestNumbers, ...newTestNumbers] : previousTestNumbers;
    });
  }, [testNumbersData]);

  const availableOptions = useMemo<IDropdownValue[]>(() => {
    const clientSpecificNumbers = new Set(
      fetchedTestNumbers.filter((testNumber) => !testNumber.isGlobalNumber).map((testNumber) => testNumber.number)
    );

    return fetchedTestNumbers
      .map((testNumber) => ({
        label: `${testNumber.name} - ${testNumber.number.toString()}`,
        value: testNumber.id,
        additionalData: {
          isGlobalNumber: testNumber.isGlobalNumber,
          number: testNumber.number,
        },
      }))
      // Filter out global numbers if a client-specific version of the same phone number exists
      .filter((tn) => !tn.additionalData.isGlobalNumber || !clientSpecificNumbers.has(tn.additionalData.number))
      .sort(compareClientSpecificFirst);
  }, [fetchedTestNumbers]);

  const optionsByNumber = useMemo(
    () => new Map<number, IDropdownValue>(availableOptions.map((option) => [option.additionalData.number, option])),
    [availableOptions]
  );

  const selectedNumbers = useMemo(() => [...new Set(value ?? [])], [value]);

  // Resolve through every fetched option rather than the latest response, and fall back to a
  // placeholder so a preselected number outside the fetched page still renders a chip and
  // survives the next onChange instead of being silently dropped
  const selectedOptions = useMemo<IDropdownValue[]>(
    () => selectedNumbers.map((number) => optionsByNumber.get(number) ?? buildPlaceholderOption(number)),
    [selectedNumbers, optionsByNumber]
  );

  // Selected options are listed first so a Select All that gets clamped at the cap keeps the
  // existing selections. The library hides options already present in `value` from the list,
  // so including them here affects Select All only, not what the user sees.
  const dropdownOptions = useMemo<IDropdownValue[]>(() => {
    const matchingOptions = searchTerm
      ? availableOptions.filter((option) => isSearchMatch(option, searchTerm))
      : availableOptions;

    return [
      ...selectedOptions,
      ...matchingOptions.filter((option) => !selectedNumbers.includes(option.additionalData.number)),
    ];
  }, [availableOptions, searchTerm, selectedOptions, selectedNumbers]);

  /**
   * Enforces the MAX_TEST_MESSAGE_NUMBERS send cap before handing the selection upward.
   * Select All hands back the entire options array, and individual clicks are unbounded, so
   * this is the only place the cap is real on the client.
   *
   * @param newValue - The selection reported by the dropdown
   */
  const handleChange = (newValue?: (IDropdownValue | undefined)[]) => {
    const selectableOptions = (newValue ?? []).filter(
      (option): option is IDropdownValue => !!option && option.additionalData?.number !== undefined
    );
    const cappedSelections = capByTestNumber(selectableOptions, (option) => option.additionalData.number);

    // The library clears its own search text on every selection; keep our filter in step
    setSearchTerm('');
    onChange(cappedSelections);
  };

  /**
   * Clears the client-side filter on blur, matching the library clearing its search text.
   */
  const handleBlur = () => {
    setSearchTerm('');
    onBlur?.();
  };

  /**
   * Handles searching test numbers based on user input.
   *
   * Numeric terms go to the server so numbers outside the fetched page stay reachable; the
   * endpoint filters on number only, so name terms are left to the client-side label filter.
   * Only fetching happens here — the client-side filter is driven by the input listener above,
   * because the library debounces this callback and skips it entirely below 2 characters.
   *
   * Memoized on `client` so the library holds the callback for the client it was rendered with.
   * That alone is not enough: the library's debounce effect depends only on its own search text
   * (@Wonder-Cave/ui/dist/index.js:6306), so it never re-arms a pending timer with a newer
   * callback. A timer scheduled just before a client change would therefore still fire the old
   * closure, and because axios-hooks shares one cancel token per hook instance, that request
   * would cancel the new client's in-flight fetch and land the *previous* client's rows in
   * `fetchedTestNumbers`. The ref check below drops that request before it is ever issued.
   *
   * @param searchValue - The search string entered by the user
   */
  const handleSearchTestNumbers = useCallback(async (searchValue: string) => {
    if (client !== latestClientRef.current) {
      return;
    }

    const searchDigits = toDigits(searchValue);
    if (!searchDigits) {
      return;
    }

    const numberFilter: ISearchFilter = {
      dataType: FilterDataTypeEnum.STRING,
      fieldName: 'number',
      operator: FilterOperatorEnum.CONTAINS,
      value: searchDigits,
    };
    const baseSearchOptions = defaultTestNumberDropdownOptions(client);
    const newSearchOptions: ISearchRequest = {
      ...baseSearchOptions,
      filters: [...baseSearchOptions.filters.filter((filter) => filter.fieldName !== 'number'), numberFilter],
    };

    try {
      // Must override `params`, not `data` — this is a GET and refetch merges onto the config
      await searchTestNumbers({ params: mapTestNumberSearchParams(newSearchOptions) });
    } catch (err) {
      // axios-hooks cancels the in-flight request whenever a newer one is issued, so every
      // superseded keystroke rejects with a CanceledError — routine, not a failure
      if (axios.isCancel(err)) {
        return;
      }
      // Log the message only — an AxiosError carries the request config, including the
      // caller's Authorization header, which must not be expandable in the browser console
      console.error(`Test number search failed: ${err instanceof Error ? err.message : err}`);
    }
  }, [client, searchTestNumbers]);

  return (
    // Wrapper exists purely so the search input can be reached by the listener above; it adds no
    // styling of its own, and AutocompleteVirtual already renders its own block-level container
    <div ref={dropdownContainerRef}>
      <AutocompleteVirtual
        label="SELECT TEST MESSAGE CONTACTS"
        placeholder="Type to search contacts"
        loading={testNumbersLoading || loading}
        value={selectedOptions}
        options={dropdownOptions}
        onChange={handleChange}
        onBlur={handleBlur}
        refreshOptions={handleSearchTestNumbers}
        error={errorMessage || testNumbersError?.message}
        disabled={disabled}
        multiple
        selectAll
        showError={showError}
      />
    </div>
  );
};

export default TestNumbersDropdown;
